import { Dialog, IconButton, Tooltip } from "@material-ui/core";
import classnames from "classnames";
import { all, clone, contains, map } from "ramda";
import * as React from "react";
import { connect } from "react-redux";
import {
  addUserToReadByForComments,
  AddUserToReadByForCommentsType
} from "src/commentView/actions";
import { AddCommentIcon } from "src/commentView/AddCommentIcon";
import { CommentIcon } from "src/commentView/CommentIcon";
import { CommentPopover } from "src/commentView/CommentPopover";
import {
  getUnreadComments,
  sortAccountCommentsByPeriod
} from "src/commentView/helpers";
import CloseIcon from "src/documentUpload/close.svg";
import { TrashIcon } from "src/documentUpload/TrashIcon";
import { getUserId } from "src/login/selector";
import { isRequesting } from "src/requestProgress/helpers";
import { actions, RequestProgressState } from "src/requestProgress/reducer";
import { getRequestProgress } from "src/requestProgress/selector";
import QuiqButton from "src/shared/QuiqButton";
import { QuiqIcon } from "src/shared/QuiqIcon";
import { State } from "src/state";
import {
  AccountComments,
  Comment,
  CommentsByPeriod
} from "../../commentView/types";
import { getIsRowHighlighted } from "../model/highlightedRows/selector";
import { AccountPresenterKind } from "../model/presenter/types";
import { getPeriodSelectedFn } from "../model/selectedCells/selector";
import {
  undoRule,
  UndoRuleActionType,
  unmapHiddenAccountRow,
  UnmapHiddenAccountRowActionType
} from "../model/spread/actions";
import {
  getAccountComments,
  getLanguage,
  getSpreadEntityId,
  getSpreadId,
  getSpreadIsCompleted
} from "../model/spread/selector";
import { RuleChangeLog, RuleHistory } from "../model/spread/types";
import AccountMetadata from "./AccountMetadata";
import ArithmeticFlyout from "./ArithmeticFlyout";
import { accountValueKey } from "./helpers";
import HiddenAccountCell from "./HiddenAccountCell";
import { UndoIcon } from "./UndoIcon";
import WarningIcon from "./warning-icon.svg";

interface HiddenAccountProps {
  account: AccountPresenterKind<"HIDDEN">;
  hierarchyLevel: number;
  periodSelected: number;
  isSpreadEditable: boolean;
  unmapHiddenAccountRowAction: UnmapHiddenAccountRowActionType;
  isHighlighted: boolean;
  isRowHighlighted: boolean;
  spreadId: string;
  userId: string;
  ruleHistory: RuleHistory;
  initialChangelog: RuleChangeLog;
  undoRuleAction: UndoRuleActionType;
  requestProgress: RequestProgressState;
  accountComments: AccountComments | null;
  periods: string[];
  onComment?: () => void;
  addUserToReadByForComments?: AddUserToReadByForCommentsType;
  parentUserEditedValues?: boolean[];
  language?: string | null;
  entityId: string;
}

interface HiddenAccountState {
  isUnmapModalOpen: boolean;
  hasUnreadComments: { [period: string]: boolean };
  commentsByPeriod: CommentsByPeriod;
  anchorElByPeriod: { [period: string]: HTMLElement | null };
}

export class HiddenAccountRow extends React.Component<
  HiddenAccountProps,
  HiddenAccountState
> {
  constructor(props: HiddenAccountProps) {
    super(props);
    this.state = {
      anchorElByPeriod: {},
      commentsByPeriod: {},
      isUnmapModalOpen: false,
      hasUnreadComments: {}
    };
  }

  public componentDidMount() {
    const { account, accountComments } = this.props;

    if (!accountComments || !accountComments[account.id]) {
      return;
    }

    const commentsByPeriod = sortAccountCommentsByPeriod(
      accountComments[account.id]
    );

    const hasUnreadCommentsByPeriod = {};

    if (this.props.periods) {
      this.props.periods.forEach(period => {
        const hasUnreadComments =
          getUnreadComments(commentsByPeriod[period] || [], this.props.userId)
            .length > 0;
        hasUnreadCommentsByPeriod[period] = hasUnreadComments;
      });
    }

    this.setState({
      hasUnreadComments: hasUnreadCommentsByPeriod,
      commentsByPeriod
    });
  }

  public render() {
    const {
      ruleHistory,
      account,
      isHighlighted,
      isRowHighlighted,
      hierarchyLevel,
      isSpreadEditable,
      periodSelected,
      entityId
    } = this.props;

    let hasNewRules = false;
    if (ruleHistory) {
      hasNewRules =
        ruleHistory[account.id] && ruleHistory[account.id].length > 0;
    }

    const isLoading =
      isRequesting(actions.undoRule) ||
      isRequesting(actions.remapAccount) ||
      isRequesting(actions.mapUnmappedAccount) ||
      isRequesting(actions.unmapHiddenAccountRow);

    const isCommenting = Object.keys(this.state.anchorElByPeriod).filter(
      anchorElKey => this.state.anchorElByPeriod[anchorElKey]
    ).length;

    const language = this.props.language;
    const name =
      (language &&
        account.translatedNames &&
        account.translatedNames[language]) ||
      account.name;

    return (
      <div
        className={classnames(
          `AccountRow__container AccountRow__container--hidden AccountRow__container--period-${account.periodValues.length}`,
          { "AccountRow--highlighted": isHighlighted },
          { "AccountRow--selected": isRowHighlighted },
          {
            "AccountRow--active-mode AccountRow--commenting-mode": isCommenting
          }
        )}
      >
        <div
          className={`AccountRow__hover-container AccountRow__hover-container--level-${hierarchyLevel}`}
        >
          <div
            className={`AccountRow__expander AccountRow__expander--hidden`}
          />
          <div className={this.nameClass(account)}>
            {name}
            <AccountMetadata account={account} />
          </div>

          {account.periodValues.map(
            (periodValue, index) => (
              <div
                key={accountValueKey(account, index) + "div"}
                className="AccountRow__text--container"
              >
                {this.props.periods && (
                  <CommentPopover
                    comments={this.commentsForPeriod(this.props.periods[index])}
                    isOpen={Boolean(
                      this.anchorElForPeriod(this.props.periods[index])
                    )}
                    onClose={this.handlePopoverClose(this.props.periods[index])}
                    anchorEl={this.anchorElForPeriod(this.props.periods[index])}
                    updateComments={this.updateNewComments(
                      this.props.periods[index]
                    )}
                    spreadId={this.props.spreadId}
                    accountId={this.props.account.id}
                    accountName={this.props.account.name}
                    dontShowAccountLabel={true}
                    period={this.props.periods[index]}
                  />
                )}

                <HiddenAccountCell
                  account={account}
                  period={index}
                  key={accountValueKey(account, index)}
                  periodValue={account.periodValues[index]}
                  parentUserEditedValues={this.props.parentUserEditedValues}
                >
                  {this.props.periods && (periodValue || periodValue === 0) && (
                    <IconButton
                      className="AccountRow__buttons__comment AccountRow__button AccountRow__buttons"
                      color={"inherit"}
                      onClick={this.openCommentPopover(
                        this.props.periods[index]
                      )}
                    >
                      {this.hasCommentsForPeriod(this.props.periods[index]) ? (
                        <React.Fragment>
                          {this.renderReadStatus(this.props.periods[index])}
                          <CommentIcon />
                        </React.Fragment>
                      ) : (
                        <AddCommentIcon
                          id={account.id}
                          className="AccountRow__button--add-comment"
                        />
                      )}
                    </IconButton>
                  )}
                </HiddenAccountCell>
              </div>
            ),
            this
          )}
          {isSpreadEditable && (
            <div className="AccountRow__map">
              <div className={classnames("AccountRow__buttons")}>
                <div className="AccountRow__map">
                  <Tooltip
                    classes={{ tooltip: "ToolTip-container" }}
                    title={hasNewRules ? "Undo" : "Remove Account"}
                    placement="top"
                  >
                    {hasNewRules ? (
                      <QuiqIcon
                        className={classnames(
                          "AccountRow__button",
                          "AccountRow__buttons__undo"
                        )}
                        children={<UndoIcon id={account.id} />}
                        color={"inherit"}
                        isDisabled={isLoading || !entityId}
                        onClick={this.handleUndoClick}
                      />
                    ) : (
                      <QuiqIcon
                        className={classnames(
                          "AccountRow__button",
                          "AccountRow__buttons__unmap"
                        )}
                        children={<TrashIcon />}
                        color={"inherit"}
                        isDisabled={isLoading}
                        onClick={this.handleOpenUnmapModal}
                      />
                    )}
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
        </div>
        {periodSelected > -1 && isSpreadEditable ? (
          <ArithmeticFlyout
            account={account}
            lens={account.valueLensesByPeriod[periodSelected]}
            period={periodSelected}
            periodValue={account.periodValues[periodSelected]}
            periodString={this.props.periods[periodSelected]}
            defaultReason="Value was read incorrectly"
          />
        ) : null}

        <Dialog
          open={this.state.isUnmapModalOpen}
          onClose={this.handleUnmapModalClose}
          className="ConfirmModal"
          PaperProps={{ className: "ConfirmModal__paper" }}
          transitionDuration={0}
          disableBackdropClick={true}
        >
          <img
            src={CloseIcon}
            className="ConfirmModal__closing-icon"
            onClick={this.handleUnmapModalClose}
          />
          <img src={WarningIcon} className="ConfirmModal__warning-icon" />
          <div className="ConfirmModal__header">Remove Account</div>
          <div className="ConfirmModal__content">
            Are you sure you want to remove this account?
          </div>
          <div className="ConfirmModal__button__container">
            <QuiqButton
              onClick={this.handleUnmapModalClose}
              variant="text"
              className="ConfirmModal__button__cancel"
              isDisabled={isLoading}
            >
              CANCEL, KEEP ACCOUNT
            </QuiqButton>
            <QuiqButton
              onClick={this.unmapAccount}
              color="blue"
              className="ConfirmModal__button__confirm"
              isLoading={isLoading}
            >
              YES, REMOVE ACCOUNT
            </QuiqButton>
          </div>
        </Dialog>
      </div>
    );
  }

  private anchorElForPeriod = (period: string) => {
    return this.state.anchorElByPeriod[period];
  };

  private commentsForPeriod = (period: string) => {
    return (period && this.state.commentsByPeriod[period]) || [];
  };

  private updateNewComments = (period: string) => (comments: Comment[]) => {
    const commentsByPeriod = clone(this.state.commentsByPeriod);
    commentsByPeriod[period] = comments;
    this.setState({
      commentsByPeriod
    });
  };

  private hasCommentsForPeriod = (period: string) => {
    return (
      this.state.commentsByPeriod[period] &&
      this.state.commentsByPeriod[period].length > 0
    );
  };

  private renderReadStatus = (period: string) => {
    const hasUnread = this.state.hasUnreadComments[period];

    return (
      <div
        className={classnames(
          "AccountRow__read-status",
          {
            "AccountRow__read-status--unread": hasUnread
          },
          {
            "AccountRow__read-status--read": !hasUnread
          }
        )}
      />
    );
  };

  private nameClass = (account: AccountPresenterKind<"HIDDEN">): string => {
    let parentUserEditedValues = this.props.parentUserEditedValues || [];
    parentUserEditedValues = parentUserEditedValues.filter(
      value => value !== null
    );

    const allPeriodsUserEdited = all(
      userEditedFlag => userEditedFlag,
      parentUserEditedValues
    );

    return classnames("AccountRow__name", "AccountRow__name--hidden", {
      "AccountRow__name--disabled": allPeriodsUserEdited
    });
  };

  private unmapAccount = () => {
    const {
      unmapHiddenAccountRowAction,
      account,
      spreadId,
      userId
    } = this.props;

    unmapHiddenAccountRowAction({
      spreadId,
      nodeId: account.id,
      userId
    });
  };

  private handleOpenUnmapModal = () => {
    this.setState({ isUnmapModalOpen: true });
  };

  private handleUnmapModalClose = () => {
    this.setState({ isUnmapModalOpen: false });
  };

  private handleUndoClick = () => {
    const {
      ruleHistory,
      account,
      undoRuleAction,
      spreadId,
      initialChangelog
    } = this.props;

    const nodeRuleHistory = ruleHistory[account.id];
    // We can only undo if we have some history
    if (nodeRuleHistory && nodeRuleHistory.length > 0) {
      // the rule we're undoing is the last in the array
      const currentRule = nodeRuleHistory[nodeRuleHistory.length - 1];
      // If we have more history than just the current rule, set previous with the history
      // Otherwise if we had a rule initially for that node when we got to the spread, set previous with that
      // Otherwise set previous to null because we're undoing a mapping back to its model-derived mapping
      const previousRule =
        nodeRuleHistory && nodeRuleHistory.length > 1
          ? nodeRuleHistory[nodeRuleHistory.length - 2]
          : initialChangelog[account.id] || null;
      undoRuleAction({ spreadId, currentRule, previousRule });
    }
  };

  private handlePopoverClose = (period: string) => (
    event?: React.SyntheticEvent<{}>
  ) => {
    const anchorElByPeriod = clone(this.state.anchorElByPeriod);

    anchorElByPeriod[period] = null;

    this.setState({
      anchorElByPeriod
    });

    this.markCommentsAsRead(period);
  };

  private openCommentPopover = (period: string) => (
    event: React.MouseEvent<HTMLElement>
  ) => {
    const anchorElByPeriod = clone(this.state.anchorElByPeriod);

    anchorElByPeriod[period] = event.currentTarget;

    this.setState({
      anchorElByPeriod
    });
  };

  private markCommentsAsRead = (period: string) => {
    const commentsByPeriod = clone(this.state.commentsByPeriod);
    const comments = commentsByPeriod[period];
    const { userId } = this.props;
    if (comments) {
      const unreadComments = getUnreadComments(comments, userId);

      if (this.props.addUserToReadByForComments) {
        this.props.addUserToReadByForComments({
          comments: unreadComments,
          userId: this.props.userId
        });
      }

      const commentsMarkedAsRead = map((comment: Comment) => {
        if (comment.readBy && !contains(userId, comment.readBy)) {
          comment.readBy.push(userId);
        }
        return comment;
      }, comments);

      const hasUnreadComments = clone(this.state.hasUnreadComments);
      hasUnreadComments[period] = false;
      commentsByPeriod[period] = commentsMarkedAsRead;

      this.setState({
        commentsByPeriod,
        hasUnreadComments
      });
    }
  };
}

const mapStateToProps = (state: State, ownProps: any) => ({
  periodSelected: getPeriodSelectedFn(state)(
    ownProps.account.spreadSheetType,
    ownProps.account.id
  ),
  isSpreadEditable: !getSpreadIsCompleted(state),
  isRowHighlighted: getIsRowHighlighted(state)(ownProps.account.id),
  spreadId: getSpreadId(state),
  userId: getUserId(state),
  requestProgress: getRequestProgress(state),
  accountComments: getAccountComments(state),
  language: getLanguage(state),
  entityId: getSpreadEntityId(state)
});

export default connect(
  mapStateToProps,
  {
    unmapHiddenAccountRowAction: unmapHiddenAccountRow.action,
    undoRuleAction: undoRule.action,
    addUserToReadByForComments: addUserToReadByForComments.action
  }
)(HiddenAccountRow);



// WEBPACK FOOTER //
// ./src/spread/chartOfAccounts/HiddenAccountRow.tsx